import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  useToast,
  Container,
  Center
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import InputComponent from "../../components/inputs";
import { FiUser, FiLock } from "react-icons/fi";
import { useLogin, clearAdminPortalSession } from "../../services/authService";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../../context/UserContext";
import RateLimitError from "../../components/RateLimitError";
import { isRateLimitError } from "../../utils/rateLimit";
import { initializeActivityTracking } from "../../utils/activityTracker";
import { triggerSessionWarningModal } from "../../context/SessionWarningContext";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { refreshUserInfo } = useUserContext();
  const [inputData, setInputData] = useState({username: "", password:""})
  const [inputDataErr, setInputDataErr] = useState({username: "", password:""})
  const [rateLimitError, setRateLimitError] = useState(null);
  
  const handleLoginSuccess = (res) => {
    const u = res?.user;
    const isEss = u?.system_role === "ess_user" || u?.role === "ess_user";
    if (isEss) {
      const essUrl = process.env.REACT_APP_ESS_APP_URL?.trim();
      if (essUrl) {
        clearAdminPortalSession();
        const target =
          essUrl.startsWith("http://") || essUrl.startsWith("https://")
            ? essUrl
            : `${window.location.origin.replace(/\/$/, "")}/${essUrl.replace(/^\//, "")}`;
        window.location.href = target;
        return;
      }

      setRateLimitError(null);

      initializeActivityTracking();

      const { getTokenExpirationTime } = require("../../utils/tokenExpiration");
      const timeUntilExpiration = getTokenExpirationTime();
      if (timeUntilExpiration) {
        const lifetimeSeconds = Math.floor(timeUntilExpiration / 1000);
        const checkInterval = setInterval(() => {
          const currentTimeLeft = getTokenExpirationTime();
          if (!currentTimeLeft) {
            clearInterval(checkInterval);
            return;
          }
          const secondsLeft = Math.floor(currentTimeLeft / 1000);
          if (secondsLeft <= 60 && secondsLeft > 0) {
            triggerSessionWarningModal();
            clearInterval(checkInterval);
          }
        }, 200);
        setTimeout(() => clearInterval(checkInterval), (lifetimeSeconds + 30) * 1000);
      }

      sessionStorage.setItem("freshLogin", "true");
      refreshUserInfo();

      const firstTime = sessionStorage.getItem("firstTimeLogin") === "true";
      navigate(firstTime ? "/ess/change-password" : "/ess");
      return;
    }

    // Clear any rate limit errors on successful login
    setRateLimitError(null);
    
    // IMPORTANT: Initialize activity tracking immediately after login
    // This ensures the session warning system can track user activity from the start
    initializeActivityTracking();
    
    // CRITICAL: Start proactive token monitoring immediately
    // Import getTokenExpirationTime locally to detect token lifetime right after login
    const { getTokenExpirationTime } = require("../../utils/tokenExpiration");
    const timeUntilExpiration = getTokenExpirationTime();
    
    if (timeUntilExpiration) {
      const lifetimeSeconds = Math.floor(timeUntilExpiration / 1000);
      
      // Start 200ms token check to trigger modal at 60 seconds remaining
      const checkInterval = setInterval(() => {
        const currentTimeLeft = getTokenExpirationTime();
        if (!currentTimeLeft) {
          clearInterval(checkInterval);
          return;
        }
        
        const secondsLeft = Math.floor(currentTimeLeft / 1000);
        
        // Show modal when token has 1 minute or less remaining
        if (secondsLeft <= 60 && secondsLeft > 0) {
          triggerSessionWarningModal();
          clearInterval(checkInterval);
        }
      }, 200);
      
      // Clean up interval after token expires (use full lifetime + 30 second buffer)
      setTimeout(() => clearInterval(checkInterval), (lifetimeSeconds + 30) * 1000);
    }
    
    // Set fresh login flag for auto-refresh
    sessionStorage.setItem('freshLogin', 'true');
    
    // Refresh user info from localStorage to update the context
    refreshUserInfo();
    // Navigate to dashboard
    navigate("/");
  };

  const handleLoginError = (error) => {
    // Check if it's a rate limit error
    if (isRateLimitError(error)) {
      const { ErrorHandler } = require("../../services/errorHandler");
      const rateLimitInfo = ErrorHandler(error);
      
      if (rateLimitInfo?.isRateLimited) {
        setRateLimitError({
          message: rateLimitInfo.message,
          retryAfter: rateLimitInfo.retryAfter,
        });
      }
    } else {
      setRateLimitError(null);
    }
  };
  
  const {loginPayload, loginIsLoading, loginError} = useLogin(handleLoginSuccess, handleLoginError);
  
  // Also check loginError for rate limit info (in case error is already processed)
  React.useEffect(() => {
    if (loginError && typeof loginError === 'object' && loginError.isRateLimited) {
      setRateLimitError({
        message: loginError.message,
        retryAfter: loginError.retryAfter,
      });
    }
  }, [loginError]);

  const handleSubmit = (e) => {
    e.preventDefault()

    // Clear previous errors
    setInputDataErr({username: "", password:""})

    // Validation
    if (!inputData.username) {
      setInputDataErr({...inputDataErr, username: t('errors.required')})
      return
    }
    
    if (!inputData.password) {
      setInputDataErr({...inputDataErr, password: t('errors.required')})
      return
    }

    const payload = {
      email: inputData.username,
      password: inputData.password,
    };

    loginPayload(payload)
  }

  return (
    <Box
      bg="gray.50"
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Container maxW="md">
        <Box
          bg="white"
          borderRadius="20px"
          p={10}
          shadow="xl"
          border="1px solid"
          borderColor="gray.100"
          minH="500px"
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {/* Header */}
          <VStack spacing={8} mb={10}>
            <Box textAlign="center">
              <Heading 
                size="xl" 
                color="gray.900" 
                fontWeight="700"
                mb={3}
              >
                {t('auth.loginTitle')}
              </Heading>
              <Text color="gray.600" fontSize="md">
                {t('auth.loginSubtitle')}
              </Text>
            </Box>
          </VStack>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <VStack spacing={6}>
              {/* Rate Limit Error Display */}
              {rateLimitError && (
                <RateLimitError
                  message={rateLimitError.message}
                  retryAfter={rateLimitError.retryAfter}
                  onComplete={() => setRateLimitError(null)}
                  width="100%"
                />
              )}
              
              <InputComponent
                id="username"
                isRequired={true}
                icon={<FiUser size={20} color="#6B7280" />}
                placeholder={t('auth.username')}
                value={inputData.username}
                handleChange={(e) => setInputData({...inputData, username: e.target.value})}
                isInvalid={inputDataErr.username ? true : false}
                errorMessage={inputDataErr.username}
                containerColor="white"
                bg="gray.50"
                borderColor="gray.200"
                _focus={{ borderColor: "blue.500", bg: "white" }}
                _hover={{ bg: "gray.100" }}
                h="56px"
                fontSize="md"
              />
              
              <InputComponent
                id="password"
                type="password"
                isRequired={true}
                icon={<FiLock size={20} color="#6B7280" />}
                placeholder={t('auth.password')}
                value={inputData.password}
                handleChange={(e) => setInputData({...inputData, password: e.target.value})}
                isInvalid={inputDataErr.password ? true : false}
                errorMessage={inputDataErr.password}
                containerColor="white"
                bg="gray.50"
                borderColor="gray.200"
                _focus={{ borderColor: "blue.500", bg: "white" }}
                _hover={{ bg: "gray.100" }}
                h="56px"
                fontSize="md"
              />

              {/* Forgot Password Link */}
              <Box width="full" textAlign="right">
                <Button
                  variant="link"
                  color="blue.500"
                  fontSize="sm"
                  fontWeight="500"
                  onClick={() => navigate("/reset-password")}
                  _hover={{ color: "blue.600", textDecoration: "underline" }}
                >
                  Forgot Password?
                </Button>
              </Box>

              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                width="full"
                mt={8}
                isLoading={loginIsLoading}
                loadingText={t('auth.loginButton') + "..."}
                borderRadius="12px"
                fontWeight="600"
                h="56px"
                fontSize="md"
                isDisabled={!!rateLimitError}
                _hover={!rateLimitError ? { transform: "translateY(-1px)", shadow: "lg" } : {}}
                transition="all 0.2s"
              >
                {rateLimitError 
                  ? `Try again in ${Math.ceil(rateLimitError.retryAfter / 60)} min` 
                  : t('auth.loginButton')}
              </Button>
            </VStack>
          </form>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
